import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';

export type StatIntelFlag = {
  flagType: string;
  status: 'ok' | 'warn' | 'critical';
};

export type StatIntelLine = {
  statLineId: string;
  playerId: string;
  firstName: string;
  lastName: string;
  displayName: string | null;
  dob: string | null;
  jerseyNumber: number | null;
  position: string | null;
  gameDate: string;
  league: string;
  team: string;
  opponent: string | null;
  isHome: number | null;
  teamScore: number | null;
  opponentScore: number | null;
  result: string | null;

  // skater stats (flat)
  goals: number | null;
  assists: number | null;
  points: number | null;
  plusMinus: number | null;
  pim: number | null;
  shots: number | null;
  hits: number | null;
  blocked: number | null;
  giveaways: number | null;
  takeaways: number | null;
  toiSeconds: number | null;

  // goalie stats (flat)
  decision: string | null;
  goalsAgainst: number | null;
  shotsAgainst: number | null;
  saves: number | null;
  savePct: number | null;
  shutout: number | null;
  source: string;
  ingestedAt: number;

  isDnp: number;
  dnpReason?: string | null;
  form?: { last5Points: number; last5Games: number; pointStreak: number } | null;
  highlights?: GoalHighlight[];
  // When someone checked "Texted" for this client and game night; null when not.
  contactedAt?: number | null;
  flags: StatIntelFlag[];
};

export type GoalHighlight = {
  role: 'goal' | 'assist';
  url: string;
  shotType: string | null;
  strength: 'ev' | 'pp' | 'sh' | null;
  period: number | null;
  timeInPeriod: string | null;
};

export type MoveFlag = {
  playerId: string;
  firstName: string;
  lastName: string;
  displayName: string | null;
  gameDate: string;
  statLeague: string;
  statTeam: string;
  fileLeague: string | null;
  fileTeam: string | null;
  direction: 'call-up' | 'send-down' | 'move';
};

export type LeagueSection = {
  key: string;
  label: string;
  sourceStatus?: 'ok' | 'stale' | 'error' | 'unverified';
  sourceError?: string | null;
  sourceCheckedAt?: number | null;
  lastIngestedAt?: number | null;
  played: StatIntelLine[];
  didNotPlay: StatIntelLine[];
};

export type StatIntelResponse = {
  leagueSections: LeagueSection[];
  lastRefreshAt: number | null;
  lastSourceCheckAt?: number | null;
  isStale?: boolean;
  gameDate: string | null;
  isTonight?: boolean;
  noGames?: boolean;
  lastGameDate?: string | null;
  moves?: MoveFlag[];
};

// Stats change once a night, so the page loads them when it opens and again when
// Scott comes back to the tab; it does not poll in the background.
export function useStatIntel(date?: string | null) {
  return useQuery<StatIntelResponse>({
    queryKey: ['stats', 'intel', date ?? 'last-night'],
    queryFn: () => apiGet<StatIntelResponse>(date ? `/api/stats/intel?date=${encodeURIComponent(date)}` : '/api/stats/intel'),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}

export type RosterMove = {
  id: string;
  playerId: string;
  name: string;
  status: 'applied' | 'review' | 'undone' | 'dismissed';
  fromLeague: string | null;
  fromTeam: string | null;
  toLeague: string;
  toTeam: string;
  evidence: string;
  detectedOn: string;
};

// Call-ups and send-downs found on the official NHL and AHL rosters each morning.
export function useRosterMoves() {
  return useQuery<{ items: RosterMove[] }>({
    queryKey: ['stats', 'roster-moves'],
    queryFn: () => apiGet<{ items: RosterMove[] }>('/api/stats/roster-moves'),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}
